import { NetworkStatus } from '@apollo/client';
import {
  AllForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  bankersRound,
  ErrorRetry,
  getUserUnitLabel,
  PersonaMessages,
  PlanningActionType,
  UnassignedForemanId,
  UnassignedGeneralForemanId,
  UnassignedSuperintendentId,
  useAppUser,
  useConfig,
  useDate,
  useName,
  usePlanning,
} from '@bechtel/client-common';
import {
  belowBreakpoint,
  Box,
  Icon,
  IconSize,
  ProgressBarMulti,
  Spinner,
  SpinnerSize,
  Text,
  theme,
  Theme,
  useMediaQuery,
} from '@bechtel/gds';
import { CrewMember, PersonaType, PlanningWeek, PlanningWeekStats, Scalars, UnitOfMeasure } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { concat, get, map, maxBy, minBy, sortBy } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { PlanningWeekCardProps } from '../../types';
import { crewMemberWeekStatMock } from './mock';
import { WeekPlanHeaderContainer, WeekPlanStyled } from './styled';
import { useWeekPlanQuery } from './use-week-plan';

function getCrewMemberInitialWeekStats(
  planningWeekStats: PlanningWeekStats[],
  persona: PersonaType,
  personaValue: string
) {
  const crewStats: PlanningWeekStats[] = [];
  map(planningWeekStats, (item: PlanningWeekStats) => {
    const weekData = {
      ...item,
      id: Math.random().toString(),
      claimedMilestoneStats: crewMemberWeekStatMock.claimedMilestoneStats,
      plannedMilestoneStats: crewMemberWeekStatMock.plannedMilestoneStats,
      committedMilestoneStats: crewMemberWeekStatMock.committedMilestoneStats,
      target: crewMemberWeekStatMock.target,
      crewMember: {
        id:
          persona === PersonaType.SUPERINTENDENT
            ? AllSuperintendentId
            : PersonaType.GENERAL_FOREMAN
              ? AllGeneralForemanId
              : AllForemanId,
        firstName: `${persona === PersonaType.SUPERINTENDENT ? t`All` : t`Multiple`}`,
        lastName: ` ${personaValue}${persona === PersonaType.SUPERINTENDENT ? 's' : ''}`,
        persona: persona,
      },
    };
    crewStats.push(weekData);
  });
  return crewStats;
}

export const PlanningWeekCard: React.FC<PlanningWeekCardProps> = ({
  planWeekStats,
  onSelectWeekMilestones,
  weekStatsRefetch,
}) => {
  const { i18n } = useLingui();
  const { numPlanningNextWeeks } = useConfig();
  const { selectedForeman, selectedGeneralForeman, selectedSuperintendent, planningDispatch } = usePlanning();
  const [
    getPlanningWeekHeader,
    {
      loading: planningWeekHeaderLoading,
      data: planningWeekHeaderData,
      error: planningWeekHeaderError,
      networkStatus: planningWeekHeaderNetworkStatus,
      refetch: planningWeekHeaderRefetch,
    },
  ] = useWeekPlanQuery();
  const [getSuperintendentWeekStats, { loading: supLoading, data: supData }] = useWeekPlanQuery();
  const [getGeneralForemanWeekStats, { loading: gfLoading, data: gfData }] = useWeekPlanQuery();
  const [getForemanWeekStats, { loading: fmLoading, data: fmData }] = useWeekPlanQuery();
  const [planningWeekHeader, setPlanningWeekHeader] = useState<PlanningWeekStats[]>([]);
  const [superintendentWeekStats, setSuperintendentWeekStats] = useState<PlanningWeekStats[]>([]);
  const [generalForemanWeekStats, setGeneralForemanWeekStats] = useState<PlanningWeekStats[]>([]);
  const [foremanWeekStats, setForemanWeekStats] = useState<PlanningWeekStats[]>([]);

  // Expose refetch functions via weekStatsRefetch ref
  useEffect(() => {
    if (weekStatsRefetch) {
      weekStatsRefetch.current = () => {
        // Only pass planningAiRequestId when status is 'COMPLETED' to include AI milestones in stats
        const aiRequestId =
          selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
            ? selectedForeman.planningAiRequest.id
            : null;

        // Refetch based on selected crew member
        if (selectedForeman && selectedForeman.id !== AllForemanId && selectedForeman.id !== UnassignedForemanId) {
          getForemanWeekStats({
            variables: {
              input: {
                persona: selectedForeman.persona || PersonaType.FOREMAN,
                crewMemberId: selectedForeman.id,
                numNextWeeks: numPlanningNextWeeks,
                ...(aiRequestId && { planningAiRequestId: aiRequestId }),
              },
            },
          });
        }
        if (
          selectedGeneralForeman &&
          selectedGeneralForeman.id !== AllGeneralForemanId &&
          selectedGeneralForeman.id !== UnassignedGeneralForemanId
        ) {
          getGeneralForemanWeekStats({
            variables: {
              input: {
                persona: selectedGeneralForeman.persona || PersonaType.GENERAL_FOREMAN,
                crewMemberId: selectedGeneralForeman.id,
                numNextWeeks: numPlanningNextWeeks,
                ...(aiRequestId && { planningAiRequestId: aiRequestId }),
              },
            },
          });
        }
        if (
          selectedSuperintendent &&
          selectedSuperintendent.id !== AllSuperintendentId &&
          selectedSuperintendent.id !== UnassignedSuperintendentId
        ) {
          getSuperintendentWeekStats({
            variables: {
              input: {
                persona: selectedSuperintendent.persona || PersonaType.SUPERINTENDENT,
                crewMemberId: selectedSuperintendent.id,
                numNextWeeks: numPlanningNextWeeks,
                ...(aiRequestId && { planningAiRequestId: aiRequestId }),
              },
            },
          });
        }
        // Always refetch header
        planningWeekHeaderRefetch({
          input: {
            numNextWeeks: numPlanningNextWeeks,
          },
        });
      };
    }
  }, [
    weekStatsRefetch,
    selectedForeman,
    selectedGeneralForeman,
    selectedSuperintendent,
    numPlanningNextWeeks,
    getForemanWeekStats,
    getGeneralForemanWeekStats,
    getSuperintendentWeekStats,
    planningWeekHeaderRefetch,
  ]);

  useEffect(() => {
    getPlanningWeekHeader({
      variables: {
        input: {
          numNextWeeks: numPlanningNextWeeks,
        },
      },
    });
  }, []);

  useEffect(() => {
    const planningWeekHeader = get(planningWeekHeaderData, 'planningWeekStats', []);
    if (planningWeekHeader && planningWeekHeader.length) {
      setPlanningWeekHeader(planningWeekHeader);
      const weekStartDate = minBy(planningWeekHeader, 'startDate')?.startDate;
      const weekEndDate = maxBy(planningWeekHeader, 'endDate')?.endDate;
      planningDispatch({
        type: PlanningActionType.SetPlanningWeekSchedule,
        payload: { weekStartDate, weekEndDate },
      });
    }
  }, [planningWeekHeaderData, planningDispatch]);

  useEffect(() => {
    if (
      selectedSuperintendent &&
      selectedSuperintendent.id &&
      selectedSuperintendent.persona &&
      !(selectedSuperintendent.id === AllSuperintendentId || selectedSuperintendent.id === UnassignedSuperintendentId)
    ) {
      const aiRequestId =
        selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
          ? selectedForeman.planningAiRequest.id
          : null;
      getSuperintendentWeekStats({
        variables: {
          input: {
            persona: selectedSuperintendent.persona,
            crewMemberId: selectedSuperintendent.id,
            numNextWeeks: numPlanningNextWeeks,
            ...(aiRequestId && { planningAiRequestId: aiRequestId }),
          },
        },
      });
    }
  }, [selectedSuperintendent, selectedForeman]);

  useEffect(() => {
    if (
      selectedSuperintendent?.id === AllSuperintendentId ||
      selectedSuperintendent?.id === UnassignedSuperintendentId
    ) {
      setSuperintendentWeekStats(
        getCrewMemberInitialWeekStats(
          planningWeekHeader,
          PersonaType.SUPERINTENDENT,
          i18n._(PersonaMessages[PersonaType.SUPERINTENDENT])
        )
      );
    } else {
      const supPlanningWeekStats = get(supData, 'planningWeekStats', superintendentWeekStats);
      setSuperintendentWeekStats(supPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = [...get(supData, 'planningWeekStats', [])];
    }
  }, [planningWeekHeader, selectedSuperintendent, supData]);

  useEffect(() => {
    if (
      selectedGeneralForeman &&
      selectedGeneralForeman.id &&
      selectedGeneralForeman.persona &&
      selectedGeneralForeman.id !== AllGeneralForemanId &&
      selectedGeneralForeman.id !== UnassignedGeneralForemanId
    ) {
      const aiRequestId =
        selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
          ? selectedForeman.planningAiRequest.id
          : null;
      getGeneralForemanWeekStats({
        variables: {
          input: {
            persona: selectedGeneralForeman.persona,
            crewMemberId: selectedGeneralForeman.id,
            numNextWeeks: numPlanningNextWeeks,
            ...(aiRequestId && { planningAiRequestId: aiRequestId }),
          },
        },
      });
    }
  }, [selectedGeneralForeman, selectedForeman]);

  useEffect(() => {
    if (
      selectedGeneralForeman?.id === AllGeneralForemanId ||
      selectedGeneralForeman?.id === UnassignedGeneralForemanId
    ) {
      setGeneralForemanWeekStats(
        getCrewMemberInitialWeekStats(
          planningWeekHeader,
          PersonaType.GENERAL_FOREMAN,
          i18n._(PersonaMessages[PersonaType.GENERAL_FOREMAN])
        )
      );
    } else {
      const generalForemanPlanningWeekStats = get(gfData, 'planningWeekStats', generalForemanWeekStats);
      setGeneralForemanWeekStats(generalForemanPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = concat(planWeekStats.current, get(gfData, 'planningWeekStats', []));
    }
  }, [planningWeekHeader, selectedGeneralForeman, gfData]);

  useEffect(() => {
    if (
      selectedForeman &&
      selectedForeman.id &&
      selectedForeman.persona &&
      selectedForeman.id !== AllForemanId &&
      selectedForeman.id !== UnassignedForemanId
    ) {
      const aiRequestId =
        selectedForeman.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
          ? selectedForeman.planningAiRequest.id
          : null;
      getForemanWeekStats({
        variables: {
          input: {
            persona: selectedForeman.persona,
            crewMemberId: selectedForeman.id,
            numNextWeeks: numPlanningNextWeeks,
            ...(aiRequestId && { planningAiRequestId: aiRequestId }),
          },
        },
      });
    }
  }, [selectedForeman, getForemanWeekStats]);

  useEffect(() => {
    if (selectedForeman?.id === AllForemanId || selectedForeman?.id === UnassignedForemanId) {
      setForemanWeekStats(
        getCrewMemberInitialWeekStats(
          planningWeekHeader,
          PersonaType.FOREMAN,
          i18n._(PersonaMessages[PersonaType.FOREMAN])
        )
      );
    } else {
      const foremanPlanningWeekStats = get(fmData, 'planningWeekStats', foremanWeekStats);
      setForemanWeekStats(foremanPlanningWeekStats);
      if (planWeekStats) planWeekStats.current = concat(planWeekStats.current, get(fmData, 'planningWeekStats', []));
    }
  }, [planningWeekHeader, selectedForeman, fmData]);
  return (
    <WeekPlanHeaderContainer>
      {(planningWeekHeaderLoading || planningWeekHeaderNetworkStatus === NetworkStatus.refetch) && (
        <Box data-testid="loading" css="width:100%;height:100%;">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Spinner size={SpinnerSize.MEDIUM} />
          </Box>
        </Box>
      )}
      {!planningWeekHeaderLoading && planningWeekHeaderError && (
        <div data-testid="retry">
          <ErrorRetry
            refetch={() =>
              planningWeekHeaderRefetch({
                input: {
                  numNextWeeks: numPlanningNextWeeks,
                },
              })
            }
          >
            <Trans>There was a problem loading planning week stats. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!planningWeekHeaderLoading && !planningWeekHeaderError && (
        <WeekPlanHeader planningWeekHeaderStats={planningWeekHeader} onSelectWeekMilestones={onSelectWeekMilestones} />
      )}
      {superintendentWeekStats && superintendentWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedSuperintendent &&
            (selectedSuperintendent.id === AllSuperintendentId ||
              selectedSuperintendent.id === UnassignedSuperintendentId)
              ? superintendentWeekStats[0].crewMember
              : selectedSuperintendent
          }
          planningWeekStats={superintendentWeekStats}
          loading={supLoading}
        />
      )}
      {generalForemanWeekStats && generalForemanWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedGeneralForeman &&
            (selectedGeneralForeman.id === UnassignedGeneralForemanId ||
              selectedGeneralForeman.id === AllGeneralForemanId)
              ? generalForemanWeekStats[0].crewMember
              : selectedGeneralForeman
          }
          planningWeekStats={generalForemanWeekStats}
          loading={gfLoading}
        />
      )}
      {foremanWeekStats && foremanWeekStats.length > 0 && (
        <WeekPlanCard
          crewMember={
            selectedForeman && (selectedForeman.id === UnassignedForemanId || selectedForeman.id === AllForemanId)
              ? foremanWeekStats[0].crewMember
              : selectedForeman
          }
          planningWeekStats={foremanWeekStats}
          loading={fmLoading}
        />
      )}
    </WeekPlanHeaderContainer>
  );
};

interface WeekPlanHeaderProps {
  planningWeekHeaderStats: PlanningWeekStats[];
  onSelectWeekMilestones?: (weekNumber: number) => void;
}

const WeekPlanHeader: React.FC<WeekPlanHeaderProps> = ({ planningWeekHeaderStats, onSelectWeekMilestones }) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const { numPlanningNextWeeks } = useConfig();
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const { formatISODate } = useDate();
  const planningWeekStatsSorted = sortBy(planningWeekHeaderStats, ['year', 'weekNumber']);
  const [openSideSheetWeekId, setOpenSideSheetWeekId] = useState<string | null>(null);
  return (
    <WeekPlanStyled>
      {map(planningWeekStatsSorted, (item: PlanningWeek, index: number) => {
        let heading = t`This Week's Plan`;
        switch (index) {
          case 1:
            heading = t`Next Week's Plan`;
            break;
          case 2:
            heading = t`Two Weeks Out`;
            break;
          case 3:
            heading = t`Three Weeks Out`;
            break;
        }
        return (
          <Box
            style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
            data-testid={`sup-week-card-container-${index}`}
            key={item.id}
          >
            <Box
              data-testid="week-card-plan-wrapper"
              flexDirection={{ sm: 'column' }}
              px={{ sm: '450' }}
              pb={{ sm: '400' }}
              pt={{ sm: '500' }}
              css={`
                width: 100%;
                color: ${({ theme }) => theme.colors.grey[1000]};
                position: relative;
              `}
            >
              <Box
                onClick={() => {
                  setOpenSideSheetWeekId(item.id);
                }}
                css={`
                  cursor: pointer;
                  position: absolute;
                  top: ${theme.spacings[500]};
                  right: ${theme.spacings[450]};
                `}
              >
                <Icon icon="ellipsis-v" size={IconSize.MEDIUM} />
              </Box>
              {openSideSheetWeekId === item.id && (
                <>
                  <Box
                    onClick={() => setOpenSideSheetWeekId(null)}
                    css={`
                      position: fixed;
                      top: 0;
                      left: 0;
                      right: 0;
                      bottom: 0;
                      z-index: 999;
                    `}
                  />
                  <Box
                    css={`
                      position: absolute;
                      top: ${theme.spacings[700]};
                      right: ${theme.spacings[450]};
                      background-color: white;
                      border: 1px solid ${({ theme }) => theme.colors.grey[300]};
                      border-radius: 4px;
                      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                      z-index: 1000;
                    `}
                  >
                    <Box
                      p={{ sm: '400' }}
                      onClick={() => {
                        onSelectWeekMilestones?.(item.weekNumber);
                        setOpenSideSheetWeekId(null);
                      }}
                      css={`
                        cursor: pointer;
                        border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        &:hover {
                          background-color: ${({ theme }) => theme.colors.grey[100]};
                        }
                      `}
                    >
                      <Text size="300" weight="400">
                        <Trans>Select all milestones</Trans>
                      </Text>
                    </Box>
                  </Box>
                </>
              )}
              <Box pb={{ sm: '300' }}>
                <Text weight="600" size={isDesktop ? '300' : '400'} family="graphik">
                  {heading}
                </Text>
              </Box>
              <Box pb={{ sm: '300' }} data-testid="week-card-number">
                <Text size={isDesktop ? '200' : '300'} weight="600">
                  <Trans>Week</Trans> {item.weekNumber}
                </Text>
                <Text size={isDesktop ? '200' : '300'} style={{ paddingLeft: `${theme.spacings[200]}` }}>
                  {item.startDate ? formatISODate(item.startDate) : t`N/A`} -{' '}
                  {item.endDate ? formatISODate(item.endDate) : t`N/A`}
                </Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </WeekPlanStyled>
  );
};

interface WeekPlanCardProps {
  crewMember: CrewMember;
  planningWeekStats: PlanningWeekStats[];
  loading: Scalars['Boolean'];
}
const WeekPlanCard: React.FC<WeekPlanCardProps> = ({ crewMember, planningWeekStats, loading }) => {
  const { numPlanningNextWeeks } = useConfig();
  const planningWeekStatsSorted = sortBy(planningWeekStats, ['year', 'weekNumber']);
  return (
    <WeekPlanStyled>
      {map(planningWeekStatsSorted, (item: PlanningWeekStats, index: number) => {
        return (
          <Box
            px={{ sm: '450' }}
            pb={{ sm: '300' }}
            style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
            data-testid={`${crewMember.persona}-week-card-container-${index}`}
            key={item.id}
          >
            <ProgressBar
              key={`${item.id}${index}${item.crewMember?.id}`}
              crewMember={crewMember}
              claimedMilestoneStats={item.claimedMilestoneStats}
              plannedMilestoneStats={item.plannedMilestoneStats}
              target={item.target}
              loading={loading}
            />
          </Box>
        );
      })}
    </WeekPlanStyled>
  );
};

interface ProgressBarProps {
  claimedMilestoneStats: PlanningWeekStats['claimedMilestoneStats'];
  plannedMilestoneStats: PlanningWeekStats['plannedMilestoneStats'];
  target: PlanningWeekStats['target'];
  crewMember?: CrewMember;
  loading: boolean;
}

interface WeekProgressDataValues {
  fillColor: string;
  label: string;
  value: number;
  labelValue?: number;
  showLegendBorder?: boolean;
  subLabel?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  claimedMilestoneStats,
  plannedMilestoneStats,
  target,
  crewMember,
  loading,
}) => {
  const { getFullName } = useName();
  const { unitOfMeasure } = usePlanning();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const data: WeekProgressDataValues[] = [];
  let showUnitPercentage = true;
  let plannedMilestoneStatsLabelValue;
  const { user } = useAppUser();
  const userUnitLabel = getUserUnitLabel(user, true);
  if (unitOfMeasure === UnitOfMeasure.LINEAR_FEET) {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.quantity.quantity
      ? plannedMilestoneStats?.quantity.quantity
      : 0;
  } else if (unitOfMeasure === 'MILESTONE') {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.numMilestones ? plannedMilestoneStats?.numMilestones : 0;
  } else {
    plannedMilestoneStatsLabelValue = plannedMilestoneStats?.hours ? plannedMilestoneStats?.hours : 0;
  }
  if (
    crewMember &&
    crewMember.id &&
    (crewMember.id === AllSuperintendentId || crewMember.id === AllGeneralForemanId || crewMember.id == AllForemanId)
  ) {
    showUnitPercentage = false;
    data.push({ label: 'N/A', value: 0, fillColor: 'white', showLegendBorder: true });
  } else {
    data.push({
      label: '',
      labelValue: bankersRound(plannedMilestoneStatsLabelValue, 0),
      value: plannedMilestoneStats && plannedMilestoneStats.hours ? plannedMilestoneStats.hours : 0,
      fillColor: 'blue.500',
      subLabel: 'To-Go',
    });
  }
  const userLabelComponent = () => {
    return (
      <Box justifyContent={{ sm: 'space-between' }} mb={{ sm: '100' }} data-testid="progress-bar-user-name">
        <Text size="200" weight="600" color="grey.1000">
          {getFullName(crewMember?.firstName, crewMember?.lastName)}
        </Text>
        {loading && <Spinner size={SpinnerSize.SMALL} style={{ marginTop: `${theme.spacings[300]}` }} />}
      </Box>
    );
  };

  let measureUnit: Scalars['String'];
  if (unitOfMeasure === UnitOfMeasure.LINEAR_FEET) {
    measureUnit = userUnitLabel === 'LM' ? 'LM' : 'LF'; // Default to LF if user unit label is set as hours or LF
  } else if (unitOfMeasure === UnitOfMeasure.MILESTONE) {
    measureUnit = 'ms';
  } else {
    measureUnit = 'h';
  }

  return (
    <Box css="width:100%" data-testid="progress-bar">
      <ProgressBarMulti
        data={data}
        label={userLabelComponent}
        totalValue={target && target.hours ? target.hours : 0}
        units={showUnitPercentage ? measureUnit : ''}
        showPercentage={!isDesktop ? showUnitPercentage : false}
        bgColor="white"
        borderColor="grey.300"
        labelColor="grey.600"
      />
    </Box>
  );
};
